import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { base_url } from "../../../api/api-url";
import { getAuthHeaders } from "../../../utility/getAuthHeaders";

// Look up whatever's already saved for this date + company + vendor.
// data will be null if nothing has been entered yet for that combination.
export const fetchDailyOrder = createAsyncThunk(
  "dailyCateringOrder/fetch",
  async ({ date, clientId, vendorId }) => {
    const headers = getAuthHeaders();
    const response = await axios.get(`${base_url}/daily-catering-order`, {
      ...headers,
      params: { date, clientId, vendorId },
    });
    return response.data; // { success, data, isToday }
  },
);

export const createDailyOrder = createAsyncThunk(
  "dailyCateringOrder/create",
  async (orderData) => {
    try {
      const response = await axios.post(
        `${base_url}/daily-catering-order`,
        orderData,
        getAuthHeaders(),
      );
      return response.data.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  },
);

export const updateDailyOrder = createAsyncThunk(
  "dailyCateringOrder/update",
  async ({ id, data }) => {
    try {
      const response = await axios.put(
        `${base_url}/daily-catering-order/${id}`,
        data,
        getAuthHeaders(),
      );
      return response.data.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  },
);

// Fetch every order recorded for a given date — used by the mobile-friendly
// report/overview screen instead of the wide spreadsheet-style table.
export const fetchOrdersByDate = createAsyncThunk(
  "dailyCateringOrder/fetchByDate",
  async ({ date, cluster }) => {
    const response = await axios.get(`${base_url}/daily-catering-order/list`, {
      ...getAuthHeaders(),
      params: { date, cluster: cluster || "cluster1" },
    });
    return {
      list: response.data.data, // array of orders
      serviceHeaders: response.data.serviceHeaders || null,
    };
  },
);

// Fetch the full-month, all-companies-side-by-side report (mirrors the old
// Excel monthly report). clientId/cluster are optional narrowing filters.
export const fetchMonthlyReport = createAsyncThunk(
  "dailyCateringOrder/fetchMonthlyReport",
  async ({ month, clientId, cluster }) => {
    const response = await axios.get(
      `${base_url}/daily-catering-order/monthly-report`,
      {
        ...getAuthHeaders(),
        params: { month, clientId: clientId || undefined, cluster: cluster || undefined },
      },
    );
    return response.data;
  },
);

// Fetch every client along with whether an order has already been placed
// for them on the given date — used to flag completed vs pending companies
// in the company dropdown.
export const fetchClientsWithOrderStatus = createAsyncThunk(
  "dailyCateringOrder/fetchClientsWithOrderStatus",
  async (date) => {
    const response = await axios.get(`${base_url}/daily-catering-order/clients`, {
      ...getAuthHeaders(),
      params: { date },
    });
    return response.data.data; // array of clients with orderPlaced/order
  },
);

const dailyCateringOrderSlice = createSlice({
  name: "dailyCateringOrder",
  initialState: {
    current: null, // the order for the currently selected date/client/vendor, or null
    layoutType: "simple", // layoutType of the selected client, from the top-level response
    layout: null, // layout tree of the selected client, only meaningful when layoutType === "advanced"
    branchSummary: null, // per top-level branch counts + status, only present when layoutType === "advanced"
    isToday: true,
    loading: false,
    saving: false,
    error: null,
    list: [], // all orders for a given date, used by the report/overview screen
    listServiceHeaders: null, // dynamic meal columns for the report table, or null to use the default set
    listLoading: false,
    listError: null,
    clientsWithStatus: [], // clients + orderPlaced flag for the selected date
    clientsWithStatusLoading: false,
    clientsWithStatusError: null,
    monthlyReport: null, // full monthly-report response, or null before first fetch
    monthlyReportLoading: false,
    monthlyReportError: null,
  },
  reducers: {
    // Call this whenever the company or vendor selection is cleared/changed
    // before a fresh fetchDailyOrder resolves, so stale data isn't shown.
    clearCurrentOrder: (state) => {
      state.current = null;
      state.layoutType = "simple";
      state.layout = null;
      state.branchSummary = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchDailyOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.data;
        state.isToday = action.payload.isToday;
        state.layoutType = action.payload.layoutType || "simple";
        state.layout = action.payload.layout || null;
        state.branchSummary = action.payload.branchSummary || null;
      })
      .addCase(fetchDailyOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // create
      .addCase(createDailyOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createDailyOrder.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(createDailyOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message;
      })
      // update
      .addCase(updateDailyOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateDailyOrder.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(updateDailyOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message;
      })
      // fetch list for report view
      .addCase(fetchOrdersByDate.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchOrdersByDate.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload.list;
        state.listServiceHeaders = action.payload.serviceHeaders;
      })
      .addCase(fetchOrdersByDate.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.error.message;
      })
      // fetch clients + order status for a given date
      .addCase(fetchClientsWithOrderStatus.pending, (state) => {
        state.clientsWithStatusLoading = true;
        state.clientsWithStatusError = null;
      })
      .addCase(fetchClientsWithOrderStatus.fulfilled, (state, action) => {
        state.clientsWithStatusLoading = false;
        state.clientsWithStatus = action.payload;
      })
      .addCase(fetchClientsWithOrderStatus.rejected, (state, action) => {
        state.clientsWithStatusLoading = false;
        state.clientsWithStatusError = action.error.message;
      })
      // fetch monthly report (all companies, spreadsheet-style)
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.monthlyReportLoading = true;
        state.monthlyReportError = null;
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.monthlyReportLoading = false;
        state.monthlyReport = action.payload;
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.monthlyReportLoading = false;
        state.monthlyReportError = action.error.message;
      });
  },
});

export const { clearCurrentOrder } = dailyCateringOrderSlice.actions;
export default dailyCateringOrderSlice.reducer;